import Box from '@mui/material/Box';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/Button';

import { State } from '../State';

/**
 * Loads when pressed, briefly, rather than sitting in the state forever: a
 * permanent progress indicator would read as this page never finishing, to
 * people and to the check that waits for every screen to finish loading.
 */
function LoadingOnPress() {
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <Button
      loading={busy}
      data-testid="gallery-button-loading-button"
      onClick={() => {
        setBusy(true);
        timer.current = setTimeout(() => setBusy(false), 1500);
      }}
    >
      {busy ? 'Saving' : 'Press to see saving'}
    </Button>
  );
}

export function ButtonExamples() {
  return (
    <>
      <State title="The four intents">
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="primary" data-testid="gallery-button-primary-button">
            Primary
          </Button>
          <Button variant="secondary" data-testid="gallery-button-secondary-button">
            Secondary
          </Button>
          <Button variant="ghost" data-testid="gallery-button-ghost-button">
            Ghost
          </Button>
          <Button variant="danger" data-testid="gallery-button-danger-button">
            Danger
          </Button>
        </Box>
      </State>

      <State title="The two sizes">
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button size="md" data-testid="gallery-button-medium-button">
            Medium
          </Button>
          <Button size="sm" data-testid="gallery-button-small-button">
            Small
          </Button>
        </Box>
      </State>

      <State title="Loading" note="While the thing it started is still on its way.">
        <LoadingOnPress />
      </State>

      <State title="Disabled">
        <Button disabled data-testid="gallery-button-disabled-button">
          Nothing to save
        </Button>
      </State>

      <State
        title="As a link"
        note="Renders an anchor, so it can be opened in a new tab."
      >
        <Button
          component={RouterLink}
          to={ROUTES.components}
          variant="secondary"
          data-testid="gallery-button-link"
        >
          A link that looks like a button
        </Button>
      </State>
    </>
  );
}
